import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "../api";
import {
  formatPct,
  formatSignedPct,
  formatSignedUsd,
  formatUsd,
} from "../utils/format";
import {
  machineLabel,
  priceSpins,
  summarizeSpins,
  type FreeFilter,
  type PnlEvent,
  type PnlMachine,
  type SpinSyncStatus,
} from "../utils/pnl";
import { Skeleton } from "./Skeleton";

interface WalletPnlProps {
  walletAddress: string;
}

type XMode = "time" | "spin";
type ChartMode = "total" | "machines";
type Range = "all" | "7d" | "30d" | "90d";

const RANGES: { id: Range; label: string; days?: number }[] = [
  { id: "7d", label: "7d", days: 7 },
  { id: "30d", label: "30d", days: 30 },
  { id: "90d", label: "90d", days: 90 },
  { id: "all", label: "All time" },
];

// Fixed series slots (validated for the dark surface). Machines are assigned
// a slot by spend once, so toggling filters never repaints survivors.
const SERIES = ["#3987e5", "#d95926", "#199e70", "#c98500", "#d55181", "#9085e9"];
const OTHER_COLOR = "#5b6982";
const PROFIT = "#3ddc97";
const LOSS = "#ff6b7a";

const dateShort = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const dateLong = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const timeAgo = (iso: string): string => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const minutesUntil = (iso: string): number =>
  Math.max(1, Math.ceil((new Date(iso).getTime() - Date.now()) / 60_000));

/** Round axis bounds outward to a "nice" step and return the tick positions. */
const niceTicks = (min: number, max: number, count = 5): { lo: number; hi: number; ticks: number[] } => {
  const span = Math.max(max - min, 1);
  const rough = span / count;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return { lo, hi, ticks };
};

const netClass = (n: number) =>
  n > 0 ? "text-green-400" : n < 0 ? "text-red-400" : "text-gray-200";

const PICKER_KEY = "cc:pnl:machines-open";
const readPickerOpen = (): boolean => {
  try {
    return localStorage.getItem(PICKER_KEY) !== "0";
  } catch {
    return true;
  }
};

/**
 * Wallet profit & loss over every recorded spin, with an optional import of
 * the wallet's real spin history (actual cost, free packs, actual buybacks).
 */
export function WalletPnl({ walletAddress }: WalletPnlProps) {
  const queryClient = useQueryClient();

  // Import progress, polled only while an import is running.
  const [watching, setWatching] = useState(false);
  const statusQuery = useQuery({
    queryKey: ["wallet-spins-status", walletAddress],
    queryFn: async () => {
      const { data, error } = await api.wallet({ walletAddress }).spins.status.get();
      if (error) throw error;
      return data.data as SpinSyncStatus;
    },
    enabled: watching,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 2500 : false),
    staleTime: 0,
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.wallet({ walletAddress }).spins.sync.post();
      if (error) throw error;
      return data.data as SpinSyncStatus;
    },
    onSuccess: (status) => {
      queryClient.setQueryData(["wallet-spins-status", walletAddress], status);
      setWatching(true);
    },
  });

  // Once an import finishes, lastSyncedAt changes and the PnL is refetched
  // with it as a cache buster.
  const liveStatus = statusQuery.data;
  const version = liveStatus?.lastSyncedAt ?? null;

  const { data, isLoading, error } = useQuery({
    queryKey: ["wallet-pnl", walletAddress, version],
    queryFn: async () => {
      const { data, error } = await api
        .wallet({ walletAddress })
        .pnl.get({ query: version ? { v: version } : {} });
      if (error) throw error;
      return data;
    },
    enabled: !!walletAddress,
    staleTime: 60_000,
  });

  const spinStatus: SpinSyncStatus | undefined = liveStatus ?? data?.data.spins;

  // Keep polling if the page loads while an import is already running
  useEffect(() => {
    if (data?.data.spins.status === "running") setWatching(true);
  }, [data?.data.spins.status]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton kind="lines" rows={2} />
        <Skeleton kind="chart" className="h-72" />
        <Skeleton kind="tiles" rows={4} />
      </div>
    );
  }
  if (error) return <p className="text-red-400">Could not load spin history.</p>;
  if (!data) return null;

  const importPanel = spinStatus && (
    <ImportPanel
      status={spinStatus}
      matched={data.data.matched}
      total={data.data.total}
      starting={syncMutation.isPending}
      startError={syncMutation.error ? "Could not start the import. Try again in a moment." : null}
      onStart={() => syncMutation.mutate()}
    />
  );

  if (data.data.events.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-gray-500">
          No spins recorded for this wallet yet. Wins show up here within a minute of landing.
        </p>
        {importPanel}
      </div>
    );
  }

  return (
    <PnlWorkbench events={data.data.events} machines={data.data.machines} importPanel={importPanel} />
  );
}

function ImportPanel({
  status, matched, total, starting, startError, onStart,
}: {
  status: SpinSyncStatus;
  matched: number;
  total: number;
  starting: boolean;
  startError: string | null;
  onStart: () => void;
}) {
  const running = status.status === "running" || starting;
  const hasData = status.spinCount > 0;
  const failed = status.status === "error" && status.lastError ? ` Last attempt failed: ${status.lastError}` : "";
  const disabledReason = status.nextRefreshAt
    ? `Up to date. You can refresh again in ${minutesUntil(status.nextRefreshAt)} min.`
    : status.busy
      ? "The server is importing other wallets right now. Try again shortly."
      : undefined;

  return (
    <div className="card-inset p-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
      <div className="flex-1 min-w-64 space-y-0.5">
        {running ? (
          <>
            <div className="text-gray-100 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              Importing spin history from Collector Crypt…
            </div>
            <div className="text-gray-400 num">
              {status.spinCount.toLocaleString()} spins in {status.pagesFetched} page
              {status.pagesFetched === 1 ? "" : "s"} so far. Large wallets take about a minute.
            </div>
          </>
        ) : hasData ? (
          <>
            <div className="text-gray-100">
              Real costs and buybacks for <span className="num">{matched.toLocaleString()}</span> of{" "}
              <span className="num">{total.toLocaleString()}</span> spins
              {!status.backfillComplete && (
                <span className="text-yellow-400"> · older spins not imported yet</span>
              )}
            </div>
            <div className="text-gray-400">
              {status.spinCount.toLocaleString()} spins on record
              {status.lastSyncedAt ? `, imported ${timeAgo(status.lastSyncedAt)}` : ""}.
              {failed}
            </div>
          </>
        ) : (
          <>
            <div className="text-gray-100">Costs are estimated from list price.</div>
            <div className="text-gray-400">
              Import this wallet's spin history from Collector Crypt to use what was actually paid
              (free packs count as $0) and what the buyback actually returned.{failed}
            </div>
          </>
        )}
        {startError && <div className="text-red-400">{startError}</div>}
      </div>
      {!running && (
        <button
          onClick={onStart}
          disabled={!status.canRefresh}
          className={`btn text-sm px-3 py-1.5 ${hasData ? "btn-secondary" : "btn-primary"} disabled:opacity-50 disabled:cursor-not-allowed`}
          title={disabledReason}
        >
          {hasData ? (status.backfillComplete ? "Refresh" : "Continue import") : "Load spin history"}
        </button>
      )}
    </div>
  );
}

function PnlWorkbench({
  events, machines, importPanel,
}: {
  events: PnlEvent[];
  machines: PnlMachine[];
  importPanel: React.ReactNode;
}) {
  // ---- View state lives in the URL so a filtered view can be shared ----
  const [params, setParams] = useSearchParams();
  const range = (RANGES.find((r) => r.id === params.get("range"))?.id ?? "all") as Range;
  const chartMode: ChartMode = params.get("chart") === "machines" ? "machines" : "total";
  const xMode: XMode = params.get("x") === "spin" ? "spin" : "time";
  const freeParam = params.get("spins");
  const freeFilter: FreeFilter = freeParam === "paid" || freeParam === "free" ? freeParam : "all";
  const [pickerOpen, setPickerOpen] = useState(readPickerOpen);
  const togglePicker = () => {
    const next = !pickerOpen;
    setPickerOpen(next);
    try {
      localStorage.setItem(PICKER_KEY, next ? "1" : "0");
    } catch {
      /* fine */
    }
  };
  const enabled = useMemo(() => {
    const m = params.get("m");
    if (m === null) return new Set(machines.map((x) => x.code));
    return new Set(m.split(",").filter(Boolean));
  }, [params, machines]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const setMachines = (codes: Iterable<string>) => {
    const set = new Set(codes);
    update({ m: set.size === machines.length ? null : [...set].join(",") });
  };
  const toggleMachine = (code: string) => {
    const next = new Set(enabled);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setMachines(next);
  };
  const soloMachine = (code: string) => setMachines([code]);
  const allOn = enabled.size === machines.length;

  // Colour slot per machine: the six machines with the most money through
  // them get their own colour, everything else shares a grey "Other" line.
  const slotByCode = useMemo(() => {
    const map = new Map<string, number>();
    [...machines]
      .sort((a, b) => b.spins * b.price - a.spins * a.price || a.code.localeCompare(b.code))
      .forEach((m, idx) => map.set(m.code, idx < SERIES.length ? idx : -1));
    return map;
  }, [machines]);
  const machineColor = (code: string) => {
    const slot = slotByCode.get(code) ?? -1;
    return slot === -1 ? OTHER_COLOR : SERIES[slot]!;
  };

  // ---- Price every spin that passes the filters ----
  const { spins, unpriced } = useMemo(() => {
    const rangeDef = RANGES.find((r) => r.id === range);
    const since = rangeDef?.days ? Date.now() - rangeDef.days * 86_400_000 : undefined;
    return priceSpins(events, machines, { enabled, since, free: freeFilter });
  }, [events, machines, enabled, range, freeFilter]);

  const stats = useMemo(() => summarizeSpins(spins), [spins]);

  // ---- Per-machine breakdown ----
  const breakdown = useMemo(() => {
    const rows = new Map<string, { machine: PnlMachine; spins: number; free: number; spent: number; returned: number; hits: number; best: number }>();
    for (const s of spins) {
      const row = rows.get(s.machine.code) ?? { machine: s.machine, spins: 0, free: 0, spent: 0, returned: 0, hits: 0, best: -Infinity };
      row.spins++;
      if (s.free) row.free++;
      row.spent += s.cost;
      row.returned += s.buyback;
      if (s.net >= 0) row.hits++;
      row.best = Math.max(row.best, s.net);
      rows.set(s.machine.code, row);
    }
    return [...rows.values()]
      .map((r) => ({ ...r, net: r.returned - r.spent, roi: r.spent > 0 ? (r.returned - r.spent) / r.spent : 0 }))
      .sort((a, b) => b.spent - a.spent);
  }, [spins]);

  const bestMachine = breakdown.length ? [...breakdown].sort((a, b) => b.net - a.net)[0] : null;
  const worstMachine = breakdown.length ? [...breakdown].sort((a, b) => a.net - b.net)[0] : null;

  // ---- Chart data ----
  const machineSeries = useMemo(() => {
    const active = machines.filter((m) => enabled.has(m.code));
    const own = active.filter((m) => (slotByCode.get(m.code) ?? -1) !== -1);
    const hasOther = active.some((m) => (slotByCode.get(m.code) ?? -1) === -1);
    return { own, hasOther };
  }, [machines, enabled, slotByCode]);

  const { chartData, yDomain, yTicks, zeroOffset } = useMemo(() => {
    let min = 0, max = 0;
    const running = new Map<string, number>();
    const chartData = spins.map((s) => {
      const key = (slotByCode.get(s.machine.code) ?? -1) === -1 ? "other" : s.machine.code;
      running.set(key, (running.get(key) ?? 0) + s.net);
      const point: Record<string, number> = { x: xMode === "time" ? s.t : s.i, k: s.i - 1, total: s.cumNet };
      if (chartMode === "machines") {
        for (const [code, value] of running) {
          point[code] = value;
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      } else {
        min = Math.min(min, s.cumNet);
        max = Math.max(max, s.cumNet);
      }
      return point;
    });
    const pad = Math.max((max - min) * 0.05, 1);
    const { lo: yMin, hi: yMax, ticks: yTicks } = niceTicks(min - pad, max + pad);
    return {
      chartData,
      yDomain: [yMin, yMax] as [number, number],
      yTicks,
      zeroOffset: Math.min(1, Math.max(0, yMax / (yMax - yMin))),
    };
  }, [spins, xMode, chartMode, slotByCode]);

  const categories = useMemo(() => {
    const groups = new Map<string | null, PnlMachine[]>();
    for (const m of machines) {
      const list = groups.get(m.category) ?? [];
      list.push(m);
      groups.set(m.category, list);
    }
    return [...groups.entries()].sort(([, a], [, b]) => {
      const spinsA = a.reduce((n, m) => n + m.spins, 0);
      const spinsB = b.reduce((n, m) => n + m.spins, 0);
      return spinsB - spinsA;
    });
  }, [machines]);

  const rangeLabel = RANGES.find((r) => r.id === range)?.label;
  const hasActual = (stats?.actualCount ?? 0) > 0;
  // Free-pack filter only makes sense once real spin records exist
  const canFilterFree = events.some((e) => e.paid !== null);
  const freeLabel = freeFilter === "paid" ? " · paid spins" : freeFilter === "free" ? " · free packs" : "";

  return (
    <div className="space-y-5">
      {/* Headline */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm text-gray-400">
            Net result{range !== "all" ? ` · last ${rangeLabel}` : ""}{freeLabel}
          </div>
          <div className={`text-4xl font-semibold num tracking-tight ${netClass(stats?.net ?? 0)}`}>
            {formatSignedUsd(stats?.net ?? 0)}
          </div>
          <div className="text-sm text-gray-400 mt-1 num">
            {stats && stats.spent > 0
              ? `${formatSignedPct(stats.roi)} on ${formatUsd(stats.spent)} spent across `
              : "Nothing spent across "}
            {spins.length.toLocaleString()} spin{spins.length === 1 ? "" : "s"}
            {stats ? `, ${dateShort(stats.first)} – ${dateShort(stats.last)}` : ""}
            {stats && hasActual && stats.actualCount < spins.length
              ? ` · ${(spins.length - stats.actualCount).toLocaleString()} still estimated`
              : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canFilterFree && (
            <div className="seg" role="group" aria-label="Spins">
              <button className={`seg-btn ${freeFilter === "all" ? "seg-btn-on" : ""}`} onClick={() => update({ spins: null })}>
                All spins
              </button>
              <button className={`seg-btn ${freeFilter === "paid" ? "seg-btn-on" : ""}`} onClick={() => update({ spins: "paid" })} title="Leave out free packs">
                Paid
              </button>
              <button className={`seg-btn ${freeFilter === "free" ? "seg-btn-on" : ""}`} onClick={() => update({ spins: "free" })} title="Only packs redeemed with points or promos">
                Free packs
              </button>
            </div>
          )}
          <div className="seg" role="group" aria-label="Range">
            {RANGES.map((r) => (
              <button
                key={r.id}
                className={`seg-btn ${range === r.id ? "seg-btn-on" : ""}`}
                onClick={() => update({ range: r.id === "all" ? null : r.id })}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {importPanel}

      {/* Machine filters */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <button
            className="inline-flex items-center gap-1.5 text-gray-300 hover:text-gray-50"
            onClick={togglePicker}
            aria-expanded={pickerOpen}
            aria-controls="pnl-machine-picker"
          >
            <span className={`text-gray-500 transition-transform ${pickerOpen ? "rotate-90" : ""}`} aria-hidden="true">
              <svg className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor"><path d="M5 2.5l6 5.5-6 5.5z" /></svg>
            </span>
            Machines
            <span className="text-gray-500 num">
              {allOn ? `all ${machines.length}` : `${enabled.size} of ${machines.length}`}
            </span>
          </button>
          {pickerOpen && (
            <>
              <button className="text-blue-400 hover:text-blue-300 disabled:text-gray-600" onClick={() => setMachines(machines.map((m) => m.code))} disabled={allOn}>
                All
              </button>
              <button className="text-blue-400 hover:text-blue-300 disabled:text-gray-600" onClick={() => setMachines([])} disabled={enabled.size === 0}>
                None
              </button>
              <span className="text-gray-500">Click to toggle, alt-click for only that machine.</span>
            </>
          )}
          {!pickerOpen && !allOn && (
            <button className="text-blue-400 hover:text-blue-300" onClick={() => setMachines(machines.map((m) => m.code))}>
              Show all
            </button>
          )}
          {unpriced > 0 && (
            <span className="text-gray-500" title="Wins without a known machine can't be priced and are left out">
              {unpriced.toLocaleString()} unpriced win{unpriced === 1 ? "" : "s"} excluded.
            </span>
          )}
        </div>
        <div id="pnl-machine-picker" className={pickerOpen ? "flex flex-wrap gap-x-4 gap-y-2" : "hidden"}>
          {categories.map(([category, list]) => {
            const onCount = list.filter((m) => enabled.has(m.code)).length;
            return (
              <div key={category ?? "uncategorized"} className="flex flex-wrap items-center gap-1.5">
                <button
                  className="text-xs text-gray-400 hover:text-gray-100 pr-1"
                  onClick={() => {
                    const next = new Set(enabled);
                    const turnOn = onCount < list.length;
                    for (const m of list) turnOn ? next.add(m.code) : next.delete(m.code);
                    setMachines(next);
                  }}
                  title={onCount < list.length ? "Enable this group" : "Disable this group"}
                >
                  {category ?? "Other"}
                </button>
                {list.map((m) => {
                  const on = enabled.has(m.code);
                  const color = machineColor(m.code);
                  return (
                    <button
                      key={m.code}
                      className={`chip ${on ? "chip-on" : "chip-off"}`}
                      style={on ? { borderColor: `${color}aa` } : undefined}
                      onClick={(e) => (e.altKey ? soloMachine(m.code) : toggleMachine(m.code))}
                      title={`${m.name} · $${m.price} per spin · ${Math.round(m.buybackRate * 100)}% buyback${m.inCatalog ? "" : " · price read from the code"}`}
                      aria-pressed={on}
                    >
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                      {machineLabel(m)}
                      <span className="text-gray-500 num">{m.spins}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {spins.length === 0 || !stats ? (
        <p className="text-gray-500 py-6 text-center">
          No spins match. Turn a machine back on or widen the range.
        </p>
      ) : (
        <>
          {/* Chart */}
          <div className="card-inset p-3 pt-4 min-w-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-1 mb-1">
              <div className="seg" role="group" aria-label="Series">
                <button className={`seg-btn ${chartMode === "total" ? "seg-btn-on" : ""}`} onClick={() => update({ chart: null })}>
                  Total
                </button>
                <button className={`seg-btn ${chartMode === "machines" ? "seg-btn-on" : ""}`} onClick={() => update({ chart: "machines" })}>
                  Per machine
                </button>
              </div>
              <div className="seg" role="group" aria-label="X axis">
                <button className={`seg-btn ${xMode === "time" ? "seg-btn-on" : ""}`} onClick={() => update({ x: null })}>
                  By date
                </button>
                <button className={`seg-btn ${xMode === "spin" ? "seg-btn-on" : ""}`} onClick={() => update({ x: "spin" })}>
                  By spin
                </button>
              </div>
            </div>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="pnl-stroke" x1="0" y1="0" x2="0" y2="1">
                      <stop offset={zeroOffset} stopColor={PROFIT} />
                      <stop offset={zeroOffset} stopColor={LOSS} />
                    </linearGradient>
                    <linearGradient id="pnl-fill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset={0} stopColor={PROFIT} stopOpacity={0.28} />
                      <stop offset={zeroOffset} stopColor={PROFIT} stopOpacity={0.02} />
                      <stop offset={zeroOffset} stopColor={LOSS} stopOpacity={0.02} />
                      <stop offset={1} stopColor={LOSS} stopOpacity={0.28} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="x"
                    type="number"
                    domain={["dataMin", "dataMax"]}
                    tickFormatter={(v: number) => (xMode === "time" ? dateShort(v) : `#${v}`)}
                    stroke="#5b6982"
                    tick={{ fill: "#8b98b0", fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={40}
                  />
                  <YAxis
                    domain={yDomain}
                    ticks={yTicks}
                    tickFormatter={(v: number) => formatSignedUsd(v)}
                    stroke="#5b6982"
                    tick={{ fill: "#8b98b0", fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    width={76}
                  />
                  <ReferenceLine y={0} stroke="#5b6982" strokeDasharray="4 4" />
                  <Tooltip
                    cursor={{ stroke: "#8b98b0", strokeDasharray: "3 3" }}
                    content={({ active, payload }) => {
                      const k = payload?.[0]?.payload?.k as number | undefined;
                      const s = k === undefined ? undefined : spins[k];
                      const point = k === undefined ? undefined : chartData[k];
                      if (!active || !s || !point) return null;
                      return (
                        <div className="rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-xs shadow-lg max-w-72">
                          <div className="text-gray-400">
                            Spin #{s.i.toLocaleString()} · {dateLong(s.t)}
                          </div>
                          <div className="text-gray-100 mt-1 truncate">{s.event.nftName}</div>
                          <div className="text-gray-400">
                            {machineLabel(s.machine)} · {s.free ? "free pack" : `paid ${formatUsd(s.cost)}`} ·{" "}
                            {s.kept ? `kept, insured ${formatUsd(s.buyback)}` : `buyback ${formatUsd(s.buyback)}`}{" "}
                            <span className={netClass(s.net)}>{formatSignedUsd(s.net)}</span>
                            {!s.actual && <span className="text-gray-500"> · estimated</span>}
                          </div>
                          {chartMode === "machines" ? (
                            <ul className="mt-1 space-y-0.5">
                              {machineSeries.own.map((m) => (
                                <li key={m.code} className="flex items-center gap-1.5 num">
                                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: machineColor(m.code) }} />
                                  <span className="text-gray-300">{machineLabel(m)}</span>
                                  <span className={`ml-auto ${netClass(point[m.code] ?? 0)}`}>{formatSignedUsd(point[m.code] ?? 0)}</span>
                                </li>
                              ))}
                              {machineSeries.hasOther && (
                                <li className="flex items-center gap-1.5 num">
                                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: OTHER_COLOR }} />
                                  <span className="text-gray-300">Other</span>
                                  <span className={`ml-auto ${netClass(point.other ?? 0)}`}>{formatSignedUsd(point.other ?? 0)}</span>
                                </li>
                              )}
                            </ul>
                          ) : (
                            <div className={`mt-1 num ${netClass(s.cumNet)}`}>Net so far {formatSignedUsd(s.cumNet)}</div>
                          )}
                        </div>
                      );
                    }}
                  />
                  {chartMode === "total" ? (
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="url(#pnl-stroke)"
                      strokeWidth={2}
                      fill="url(#pnl-fill)"
                      dot={false}
                      isAnimationActive={false}
                      baseValue={0}
                    />
                  ) : (
                    <>
                      {machineSeries.own.map((m) => (
                        <Line
                          key={m.code}
                          type="monotone"
                          dataKey={m.code}
                          stroke={machineColor(m.code)}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                          connectNulls
                        />
                      ))}
                      {machineSeries.hasOther && (
                        <Line
                          type="monotone"
                          dataKey="other"
                          stroke={OTHER_COLOR}
                          strokeWidth={1.5}
                          strokeDasharray="4 3"
                          dot={false}
                          isAnimationActive={false}
                          connectNulls
                        />
                      )}
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {chartMode === "machines" && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 pt-2 text-xs text-gray-400">
                {machineSeries.own.map((m) => (
                  <span key={m.code} className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-0.5 rounded" style={{ background: machineColor(m.code) }} />
                    {machineLabel(m)}
                  </span>
                ))}
                {machineSeries.hasOther && (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-0.5 rounded" style={{ background: OTHER_COLOR }} />
                    Other machines
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Totals */}
          <dl className={`grid grid-cols-3 sm:grid-cols-4 ${hasActual ? "lg:grid-cols-8" : "lg:grid-cols-6"} gap-x-3 gap-y-3 text-sm card-inset p-4`}>
            <Stat label="Spent" value={formatUsd(stats.spent)} hint={hasActual ? "What was actually paid; free packs count as $0" : "List price of every spin"} />
            <Stat label={hasActual ? "Returned" : "Bought back"} value={formatUsd(stats.returned)} hint={hasActual ? "What the instant buyback paid out, plus the insured value of cards that were kept" : "Sum of buyback rate × insured value"} />
            {hasActual && (
              <Stat
                label="Free packs"
                value={stats.freeCount.toLocaleString()}
                hint={`Spins paid with points or promos, worth ${formatUsd(stats.freeValue)} at list price`}
                tone={stats.freeCount > 0 ? 1 : 0}
              />
            )}
            {hasActual && (
              <Stat label="Kept cards" value={stats.keptCount.toLocaleString()} hint="Cards not sold back; counted at their insured value" />
            )}
            <Stat label="Hit rate" value={formatPct(stats.hitRate)} hint="Spins whose buyback covered the spin price" />
            <Stat label="Avg per spin" value={formatSignedUsd(stats.avgNet)} tone={stats.avgNet} />
            <Stat label="Peak" value={formatSignedUsd(stats.peak)} tone={stats.peak} hint="Highest the running total got" />
            <Stat label="Max drawdown" value={`−${formatUsd(stats.drawdown)}`} tone={stats.drawdown > 0 ? -1 : 0} hint="Largest drop from a high point" />
          </dl>

          {/* Highlights */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Highlight
              label="Biggest win"
              value={formatSignedUsd(stats.best.net)}
              tone={stats.best.net}
              detail={`${stats.best.event.nftName} · ${machineLabel(stats.best.machine)} · ${dateShort(stats.best.t)}`}
              image={stats.best.event.nftImage}
            />
            <Highlight
              label="Worst spin"
              value={formatSignedUsd(stats.worst.net)}
              tone={stats.worst.net}
              detail={`${stats.worst.event.nftName} · ${machineLabel(stats.worst.machine)} · ${dateShort(stats.worst.t)}`}
              image={stats.worst.event.nftImage}
            />
            <Highlight
              label="Best machine"
              value={bestMachine ? formatSignedUsd(bestMachine.net) : "—"}
              tone={bestMachine?.net ?? 0}
              detail={bestMachine ? `${machineLabel(bestMachine.machine)} · ${formatSignedPct(bestMachine.roi)} over ${bestMachine.spins} spins` : ""}
              image={bestMachine?.machine.thumbnailUrl}
            />
            <Highlight
              label="Worst machine"
              value={worstMachine ? formatSignedUsd(worstMachine.net) : "—"}
              tone={worstMachine?.net ?? 0}
              detail={worstMachine ? `${machineLabel(worstMachine.machine)} · ${formatSignedPct(worstMachine.roi)} over ${worstMachine.spins} spins · longest cold streak ${stats.worstCold}` : ""}
              image={worstMachine?.machine.thumbnailUrl}
            />
          </div>

          {/* Per-machine breakdown */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="table-head">
                  <th className="pb-2 font-medium">Machine</th>
                  <th className="pb-2 font-medium text-right">Spins</th>
                  {hasActual && <th className="pb-2 font-medium text-right">Free</th>}
                  <th className="pb-2 font-medium text-right">Spent</th>
                  <th className="pb-2 font-medium text-right">{hasActual ? "Returned" : "Bought back"}</th>
                  <th className="pb-2 font-medium text-right">Net</th>
                  <th className="pb-2 font-medium text-right">Return</th>
                  <th className="pb-2 font-medium text-right">Hit rate</th>
                  <th className="pb-2 font-medium text-right">Best spin</th>
                  <th className="pb-2 font-medium text-right hidden sm:table-cell">Buyback</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {breakdown.map((row) => (
                  <tr key={row.machine.code} className="table-row">
                    <td className="py-2">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: machineColor(row.machine.code) }} />
                        <span className="text-gray-100">{machineLabel(row.machine)}</span>
                        <span className="text-gray-500 num">${row.machine.price}</span>
                        {row.machine.status === "closed" && <span className="pill bg-gray-700 text-gray-400">closed</span>}
                      </span>
                    </td>
                    <td className="py-2 text-right num text-gray-300">{row.spins.toLocaleString()}</td>
                    {hasActual && <td className="py-2 text-right num text-gray-300">{row.free ? row.free.toLocaleString() : "—"}</td>}
                    <td className="py-2 text-right num text-gray-300">{formatUsd(row.spent)}</td>
                    <td className="py-2 text-right num text-gray-300">{formatUsd(row.returned)}</td>
                    <td className={`py-2 text-right num font-medium ${netClass(row.net)}`}>{formatSignedUsd(row.net)}</td>
                    <td className={`py-2 text-right num ${netClass(row.net)}`}>{formatSignedPct(row.roi)}</td>
                    <td className="py-2 text-right num text-gray-300">{formatPct(row.hits / row.spins)}</td>
                    <td className="py-2 text-right num text-gray-300">{formatSignedUsd(row.best)}</td>
                    <td className="py-2 text-right num text-gray-500 hidden sm:table-cell">{Math.round(row.machine.buybackRate * 100)}%</td>
                    <td className="py-2 text-right">
                      <button className="text-xs text-blue-400 hover:text-blue-300" onClick={() => soloMachine(row.machine.code)}>
                        Only
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, hint, tone = 0 }: { label: string; value: string; hint?: string; tone?: number }) {
  return (
    <div title={hint}>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`num font-medium ${netClass(tone)}`}>{value}</dd>
    </div>
  );
}

function Highlight({
  label, value, detail, tone, image,
}: {
  label: string;
  value: string;
  detail: string;
  tone: number;
  image?: string | null;
}) {
  return (
    <div className="card-inset p-3 flex items-center gap-3 min-w-0">
      {image ? (
        <img src={image} alt="" className="w-10 h-14 rounded object-cover shrink-0 border border-gray-600/60" loading="lazy" />
      ) : (
        <span className="w-10 h-14 rounded shrink-0 bg-gray-700" />
      )}
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className={`text-lg font-semibold num ${netClass(tone)}`}>{value}</div>
        <div className="text-xs text-gray-400 truncate" title={detail}>{detail}</div>
      </div>
    </div>
  );
}
